import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { IDropZone } from './types';
import { mapToCssModules } from 'utils/mapToCss';



export const Dropzone = ({
  id,
  disabled = false,
  cbOnFilesAdded,
  collapsed = false,
  acceptAllMIMETypes = true,
  textDesktop,
  textMobile,
  textDesktopDrop,
  extensions = ['image/jpg', 'image/jpeg', 'image/png', 'application/pdf', 'text/csv'],
  textExtensions = '(.jpg, .jpeg, .png, .pdf, .csv)',
  className = '',
}: IDropZone) => {
  const [highlight, setHighlight] = useState<boolean>(false);
  const [isDesktop, setIsDesktop] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const MimeTypes = useMemo<string>(() => {
    if (acceptAllMIMETypes) {
      return ''; 
    }
    return extensions.join(','); 
  }, [extensions, acceptAllMIMETypes]);

  const verifyIfIsDesktop = useCallback((width: number, height: number) => {
    setIsDesktop(!(width < 600 || height < 600));
  }, []);

  useEffect(() => {
    verifyIfIsDesktop(window.innerWidth, window.innerHeight);
    window.addEventListener('resize', () => {
      verifyIfIsDesktop(window.innerWidth, window.innerHeight);
    });
  }, [verifyIfIsDesktop]);

  function fileListToArray(list: FileList): File[] {
    const array = Array.from(list);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (acceptAllMIMETypes) {
      return array;
    }

    return array.filter((file) => extensions.includes(file?.type));
  }

  function openFileDialog() {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }

  function onFilesAdded(event: React.ChangeEvent<HTMLInputElement>): void {
    if (disabled) {
      return;
    }
    const files = event.target.files;
    if (cbOnFilesAdded) {
      const array = fileListToArray(files);
      cbOnFilesAdded(array);
    }
  }

  function onDragOver(event: React.DragEvent): void {
    event.preventDefault();
    if (!disabled) {
      setHighlight(true);
    }
  }

  function onDragLeave() {
    setHighlight(false);
  }

  function onDrop(event: React.DragEvent) {
    event.preventDefault();
    if (disabled) {
      return;
    }

    const files = event.dataTransfer.files;
    if (cbOnFilesAdded) {
      const array = fileListToArray(files);
      cbOnFilesAdded(array);
    }

    setHighlight(false);
  }

  return (
    <div
      id={id}
      className={mapToCssModules([
        'vbz-dropzone',
        highlight ? 'highlight' : '',
        isDesktop && !collapsed ? 'full' : '',
        disabled ? 'disabled' : '',
        className,
      ])}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={openFileDialog}
      style={{ cursor: disabled ? 'default' : 'pointer' }}
    >
      <input
        ref={fileInputRef}
        disabled={disabled}
        className="file-input"
        data-testid="file-input"
        type="file"
        accept={MimeTypes}
        multiple
        onChange={onFilesAdded}
      />
      {!highlight && <i className="material-icons">attach_file</i>}

      <div className="text-default">
        {highlight ? (
          <span>{textDesktopDrop}</span>
        ) : (
          <>
            <span>{isDesktop ? textDesktop : textMobile}</span>
            <span>{textExtensions}</span>
          </>
        )}
      </div>
    </div>
  );
};
